<template>
    <div>
        <div class="q-mt-lg">
            <span
                class="text-h7 text-weight-bold text-uppercase text-secondary"
                >{{ $t('Explore the data') }}</span
            >
        </div>
        <div class="row q-col-gutter-md select-card-margin">
            <div class="col-lg-3 col-md-4 col-sm-6 col-12">
                <report-select-card
                    id="overall-kpi-button"
                    @click="selectReport('kpi')"
                    :title="$t('Overall KPI')"
                    icon="fa fa-tachometer-alt"
                    :description="
                        $t(
                            'Detailed production metrics for facilities and assets.'
                        )
                    "
                />
            </div>
            <div class="col-lg-3 col-md-4 col-sm-6 col-12">
                <report-select-card
                    id="kpi-trends-button"
                    @click="selectReport('trends')"
                    :title="$t('Trends')"
                    icon="fa fa-chart-area"
                    :description="
                        $t('Discover how production is trending over time.')
                    "
                />
            </div>
            <div class="col-lg-3 col-md-4 col-sm-6 col-12">
                <report-select-card
                    id="losses-button"
                    @click="selectReport('losses')"
                    :title="$t('Losses')"
                    icon="fa fa-exclamation-circle"
                    :description="
                        $t(
                            'Identify the top causes of downtimes with Pareto analysis.'
                        )
                    "
                />
            </div>
            <div class="col-lg-3 col-md-4 col-sm-6 col-12">
                <report-select-card
                    id="data-entry-button"
                    @click="selectReport('data_entry_performance')"
                    :title="$t('Data Entry Performance')"
                    icon="fas fa-map"
                    :description="
                        $t('Calculate the data entry score for each period.')
                    "
                />
            </div>
            <div class="col-lg-3 col-md-4 col-sm-6 col-12">
                <report-select-card
                    id="maintenance-button"
                    @click="selectReport('maintenance')"
                    :title="$t('Maintenance')"
                    icon="fas fa-wrench"
                    :description="
                        $t('Understand Equipment Failures & Repair Speed.')
                    "
                />
            </div>
            <div class="col-lg-3 col-md-4 col-sm-6 col-12">
                <report-select-card
                    id="changeover-button"
                    @click="selectReport('changeover')"
                    :title="$t('Changeovers')"
                    icon="fas fa-sync-alt"
                    :description="
                        $t(
                            'Explore changeovers to identify problematic transitions between pairs of products or product attributes.'
                        )
                    "
                />
            </div>
            <div
                v-if="isTrackingQualityEvents"
                class="col-lg-3 col-md-4 col-sm-6 col-12"
            >
                <report-select-card
                    id="quality-button"
                    @click="selectReport('quality')"
                    :title="$t('Quality')"
                    icon="fas fa-clipboard-list-check"
                    :description="
                        $t(
                            'Review quality issues and identify the most common defects.'
                        )
                    "
                />
            </div>
            <div class="col-lg-3 col-md-4 col-sm-6 col-12">
                <report-select-card
                    id="benchmark-button"
                    @click="selectReport('benchmark')"
                    :title="$t('Benchmark')"
                    icon="fa-solid fa-magnifying-glass-chart"
                    :description="
                        $t(
                            'Compare OEE and downtime across facilities or facility groups to identify gaps, benchmark efficiency, and drive improvement.'
                        )
                    "
                />
            </div>
        </div>
        <div class="q-mt-lg">
            <span
                class="text-h7 text-weight-bold text-uppercase text-secondary"
                >{{ $t('Automatic Reports') }}</span
            >
        </div>
        <div class="row q-col-gutter-md select-card-margin">
            <div class="col-lg-3 col-md-4 col-sm-6 col-12">
                <report-select-card
                    id="automatic-reports-button"
                    @click="
                        $router.push({
                            name: 'automatic_report_list'
                        })
                    "
                    :title="$t('Automatic Reports')"
                    icon="fas fa-calendar-clock"
                    :description="$t('Receive reports automatically by email')"
                />
            </div>
        </div>
        <div class="q-mt-lg">
            <span
                class="text-h7 text-weight-bold text-uppercase text-secondary"
                >{{ $t('Raw Data') }}</span
            >
        </div>
        <div class="row q-col-gutter-md select-card-margin">
            <div class="col-lg-3 col-md-4 col-sm-6 col-12">
                <report-select-card
                    id="export-phases-report-button"
                    @click="showPhasesReportModal = true"
                    :title="$t('Download Phases')"
                    icon="fas fa-exclamation-circle"
                    :description="
                        $t('Download phases from a specific facility')
                    "
                />
            </div>
        </div>

        <!-- Dialog box for export phases report -->
        <export-phases
            :facilityId="facility?.id"
            :headerText="$t('Download Phases')"
            :selectFacility="true"
            v-if="showPhasesReportModal"
            @close="showPhasesReportModal = false"
        />
    </div>
</template>

<script>
import ExportPhases from '@components/ExportPhases.vue';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import { mapState } from 'pinia';
import { useSettingsStore } from '@stores/settings.js';
import { useFacilitiesStore } from '@stores/facilities';
import ReportSelectCard from '../../components/ReportSelectCard.vue';
export default {
    name: 'ReportSelect',
    components: { ReportSelectCard, ExportPhases },
    mixins: [BreadcrumbsManager],
    /**
     * Hook to extract the url query param so that
     * we can assign the facility id to the data
     * property of this component. This is done
     * because on going back via back button on
     * url this page looses the facility param
     */
    beforeRouteEnter(to, from, next) {
        const facilityIdQueryParam = to.query?.facility;
        next(component => {
            component.facilityId = parseInt(facilityIdQueryParam);
        });
    },
    async mounted() {
        if (Number.isInteger(this.facilityId)) {
            var breadcrumbs = [
                {
                    label: this.$t('Facilities'),
                    route: {
                        name: 'facility_list'
                    }
                },
                {
                    label: this.facility.name,
                    route: {
                        name: 'facility_dashboard',
                        params: {
                            facilityId: parseInt(this.facility.id)
                        }
                    }
                },
                {
                    label: this.$t('Reports')
                }
            ];
        } else {
            breadcrumbs = [
                {
                    label: this.$t('Facilities'),
                    route: {
                        name: 'facility_list'
                    }
                },
                {
                    label: this.$t('Reports')
                }
            ];
        }
        // set breadcrumbs
        this.setBreadcrumbs(breadcrumbs);
    },
    data() {
        return {
            showPhasesReportModal: false
        };
    },
    methods: {
        /**
         * Handles report selection.
         * @param report
         */
        selectReport(report) {
            this.$router.push({
                name: report,
                query: this.facilityId
                    ? {
                          facility: parseInt(this.facilityId)
                      }
                    : null
            });
        }
    },
    computed: {
        ...mapState(useSettingsStore, ['isTrackingQualityEvents']),
        ...mapState(useFacilitiesStore, ['getFacility']),
        /**
         * Get facility details.
         * @return {Object} - Facility details.
         */
        facility() {
            return this.getFacility(parseInt(this.facilityId));
        }
    }
};
</script>

<style lang="scss" scoped>
.select-card-margin {
    margin-top: unset;
}
</style>
